<template>
  <div class="day-view">
    <div v-if="isArrivalOrder" class="arrival-order-info">
      <fa-icon :icon="['fal', 'clock']" />
      <span>Agenda por ordem de chegada</span>
      <strong class="chip ml-2 bg-info">
        {{ displayArrivalRange.start }} - {{ displayArrivalRange.end }}
      </strong>
    </div>
    <div class="day-view-table-container" ref="tableContainer">
      <table class="table table-hover">
        <tbody>
          <table-row
            v-for="(appointment, index) in appointments"
            :key="appointment.key || index"
            :data="appointment"
            v-on="tableRowListeners"
            :ref="`row-${appointment.key || index}`"
          />
        </tbody>
      </table>
      <div
        v-if="currentTimeStyle"
        class="day-view-current-time-line"
        :style="currentTimeStyle"
      >
      </div>
      <div
        v-if="currentTimeStyle"
        class="day-view-current-time"
        :style="currentTimeStyle"
      >
        <span class="day-view-current-time-label">{{ currentTimeLabel }}</span>
      </div>
    </div>
  </div>
</template>

<script>
import moment from 'moment';
import { mapState } from 'vuex';
import TableRow from '../TableRow.vue';

export default {
  name: 'DayView',
  components: {
    TableRow,
  },
  props: {
    appointments: {
      type: Array,
      default: () => [],
    },
    isArrivalOrder: {
      type: Boolean,
      default: false,
    },
    arrivalOrderRange: {
      type: Object,
      default: null,
    },
    date: {
      type: String,
      default: '',
    },
  },
  data() {
    return {
      currentTime: null,
      timeUpdateInterval: null,
      secondUpdateInterval: null,
      rowHeights: [],
    };
  },
  computed: {
    ...mapState({
      nowState: ({ now }) => now,
    }),
    displayArrivalRange() {
      return this.arrivalOrderRange || { start: '', end: '' };
    },
    nowMoment() {
      if (this.currentTime) {
        return moment(this.currentTime);
      }
      return this.nowState ? moment(this.nowState) : moment();
    },
    currentTimeLabel() {
      return this.nowMoment.format('HH:mm');
    },
    targetMoment() {
      if (!this.date) {
        return this.nowMoment;
      }
      const timePart = this.nowMoment.format('HH:mm');
      return moment(`${this.date}T${timePart}`);
    },
    timeRange() {
      let minHour = 12;
      let maxHour = 24;

      if (this.appointments && this.appointments.length > 0) {
        const allTimes = [];

        this.appointments.forEach((appointment) => {
          if (appointment.startDate) {
            const start = moment(appointment.startDate);
            if (start.isValid()) {
              const hour = start.hour();
              const minute = start.minute();
              allTimes.push(hour * 60 + minute);
            }
          }
          if (appointment.endDate) {
            const end = moment(appointment.endDate);
            if (end.isValid()) {
              const hour = end.hour();
              const minute = end.minute();
              allTimes.push(hour * 60 + minute);
            }
          }
        });

        if (allTimes.length > 0) {
          const minMinutes = Math.min(...allTimes);
          const maxMinutes = Math.max(...allTimes);

          const calculatedMinHour = Math.floor(minMinutes / 60);
          const calculatedMaxHour = Math.ceil(maxMinutes / 60);

          if (calculatedMinHour < 12) {
            minHour = calculatedMinHour;
          } else if (calculatedMinHour === 12) {
            minHour = 12;
          } else {
            minHour = calculatedMinHour - 1;
          }

          maxHour = Math.max(calculatedMaxHour, 12);
        } else {
          minHour = 12;
          maxHour = 18;
        }
      } else {
        minHour = 12;
        maxHour = 18;
      }

      return { minHour, maxHour };
    },
    currentTimeStyle() {
      if (!this.date || this.appointments.length === 0 || this.rowHeights.length === 0) {
        return null;
      }

      const targetDate = moment(this.date);
      const today = moment().startOf('day');
      if (!targetDate.isSame(today, 'day')) {
        return null;
      }

      const currentTimeMinutes = this.targetMoment.hour() * 60 + this.targetMoment.minute();

      let offset = 0;

      for (let i = 0; i < this.appointments.length; i += 1) {
        const appointment = this.appointments[i];
        const appointmentStart = moment(appointment.startDate);
        const rowHeight = this.rowHeights[i] || 60;

        if (appointmentStart.isValid()) {
          const startMinutes = appointmentStart.hour() * 60 + appointmentStart.minute();

          if (currentTimeMinutes < startMinutes) {
            return {
              top: `${offset}px`,
            };
          }

          if (currentTimeMinutes === startMinutes) {
            return {
              top: `${offset + (rowHeight / 2)}px`,
            };
          }

          if (currentTimeMinutes > startMinutes) {
            offset += rowHeight;
          }
        } else {
          offset += rowHeight;
        }
      }

      return {
        top: `${offset}px`,
      };
    },
    tableRowListeners() {
      return {
        deleteMultipleAppointments: this.onDeleteMultipleAppointments,
        openModalInsuranceList: this.onOpenModalInsuranceList,
        openModalExpenses: this.onOpenModalExpenses,
        infoWaitingList: this.onInfoWaitingList,
      };
    },
  },
  methods: {
    updateCurrentTime() {
      this.currentTime = new Date();
      this.$nextTick(() => {
        this.calculateRowHeights();
      });
    },
    calculateRowHeights() {
      if (!this.$refs.tableContainer) return;

      this.rowHeights = this.appointments.map((appointment, index) => {
        const ref = this.$refs[`row-${appointment.key || index}`];
        if (ref && ref[0] && ref[0].$el) {
          return ref[0].$el.offsetHeight;
        }
        return 60;
      });
    },
    onDeleteMultipleAppointments(payload) {
      this.$emit('deleteMultipleAppointments', payload);
    },
    onOpenModalInsuranceList(payload) {
      this.$emit('openModalInsuranceList', payload);
    },
    onOpenModalExpenses(payload) {
      this.$emit('openModalExpenses', payload);
    },
    onInfoWaitingList() {
      this.$emit('infoWaitingList');
    },
  },
  mounted() {
    this.updateCurrentTime();
    this.timeUpdateInterval = setInterval(() => {
      this.updateCurrentTime();
    }, 60000);
    this.secondUpdateInterval = setInterval(() => {
      this.updateCurrentTime();
    }, 1000);

    this.$nextTick(() => {
      this.calculateRowHeights();
    });
  },
  beforeDestroy() {
    if (this.timeUpdateInterval) {
      clearInterval(this.timeUpdateInterval);
      this.timeUpdateInterval = null;
    }
    if (this.secondUpdateInterval) {
      clearInterval(this.secondUpdateInterval);
      this.secondUpdateInterval = null;
    }
  },
  watch: {
    appointments: {
      handler() {
        this.$nextTick(() => {
          this.calculateRowHeights();
        });
      },
      deep: true,
    },
  },
};
</script>

<style lang="scss">
@import '../../../../assets/scss/variables';

.day-view-table-container {
  position: relative;
}

.day-view-current-time {
  position: absolute;
  left: -5px;
  right: 0;
  display: flex;
  align-items: center;
  justify-content: flex-start;
  pointer-events: none;
  z-index: 10;
  padding-left: 0.25rem;
  transform: translateY(-50%);
}

.day-view-current-time-label {
  background-color: $error-color;
  color: $light-color;
  font-size: $font-size-xs;
  font-weight: 500;
  padding: 0.125rem 0.375rem;
  border-radius: $border-radius;
  white-space: nowrap;
}

.day-view-current-time-line {
  position: absolute;
  left: 0;
  right: 0;
  pointer-events: none;
  z-index: 10;
  border-top: 0.08rem solid $error-color;
  transform: translateY(-50%);
}
</style>
